import { SlotEvent, SystemEvent, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { isHTMLElement, isNotUndefined } from '@mbrtargeting/metatag-utils';
import { triggerEvent, triggerHTMLElementEvent, waitForEventPromise } from '../../../loader/essentials/events.js';
import { SlotEffects } from '../../effects/index.js';
import { eventBus$ } from '../../observables/event-bus.js';
import { StateMachine } from '../../state-machine.js';
import { Adslot } from './adslot.js';

export enum QueueState {
    /**
     * Used to signal the slot is inert, publisher did not send a load command.
     */
    IDLE = 'IDLE',
    /**
     * Used to signal the slot should get loaded (publisher sent load command).
     * Slot has passed basic checks and is now queued and waiting for ad call.
     */
    QUEUED = 'QUEUED',
    /**
     * AdSlot is ready to be filled by an AdServer, after the slot:
     *   - has received targetings
     *   - lazyload is disabled or lazyload slot got visible
     */
    READY = 'READY',
    /**
     * AdSlot is being processed by adserver implementations.
     */
    PROCESSING = 'PROCESSING',
    /**
     * A valid response from any ad server source has been received.
     * Some basic information about the response is available (maybe the size of creative)
     * and can be communicated to the publisher.
     */
    DONE = 'DONE',
}

export enum QueueAction {
    ENQUEUE = 'ENQUEUE',
    GETREADY = 'GETREADY',
    PROCESS = 'PROCESS',
    COMPLETE = 'COMPLETE',
}

export interface QueueContext {
    runAuction: boolean;
}

export class AdslotQueueState extends StateMachine<QueueState, QueueAction, QueueContext> {

    constructor(adslot: Adslot) {
        super({
            logPrefix: `QueueState ${adslot.slotName.toUpperCase()}`,
            initialState: QueueState.IDLE,
            transitions: [
                { from: QueueState.IDLE, action: QueueAction.ENQUEUE, to: QueueState.QUEUED },
                { from: QueueState.QUEUED, action: QueueAction.GETREADY, to: QueueState.READY },
                { from: QueueState.READY, action: QueueAction.PROCESS, to: QueueState.PROCESSING },
                { from: QueueState.PROCESSING, action: QueueAction.COMPLETE, to: QueueState.DONE },
                { from: QueueState.DONE, action: QueueAction.ENQUEUE, to: QueueState.QUEUED },
            ],
            initialContext: {
                runAuction: true,
            },
        });
        //used in most events as DetailMap concerning this slot
        const slotEventDetails = {
            passedObject: adslot,
            placement: adslot,
            slot: adslot.slotName,
            position: adslot.slotName,
            slotName: adslot.slotName,
        };

        // wait for delivery phase before sending adslots to adserver
        this.onAction(QueueAction.GETREADY, async () => waitForEventPromise(SystemEvent.DELIVERY_PHASE_STARTED));

        // can slot be queued? - check lazy loading
        this.onAction(QueueAction.GETREADY, async () => new Promise<void>((resolve) => {
            const { lazyLoad } = adslot.options;
            if (!lazyLoad) return resolve();
            eventBus$.find(event => event.type === SystemSlotEvent.SDG_SLOT_LAZY_LOAD_READY && event.slotId === adslot.slotId).then(() => resolve());
        }));

        // before sending the slot to the adserver, make it visible again
        this.onEnter(QueueState.READY, async () => {
            adslot.toggleEffect(SlotEffects.REMOVE_SLOT_CONTAINER, false);
            adslot.toggleEffect(SlotEffects.HIDE_SLOT_CONTAINER, false);
        });

        /**
         * signals adslot will now be passed to all available creative sources and tries to find a suitable campaign.
         * Unload all former ads (if slot was reloaded), and revert any leftover changes from former impression
         */
        this.onState(QueueState.PROCESSING, async () => {
            adslot.advert?.unloadAd(); // cleanup  ad library advertisement if present

            /*
             * Remove all direct descendant nodes in adServerContainer, which were not created through createStableElement()
             * clear divs marked as "emptyOnReload" completely, but let div itself remain
             */
            Array.from(adslot.adserverContainer.childNodes).map(elem => {
                if (!isHTMLElement(elem)) return elem;

                const stableElement = elem.getAttribute('data-stable-element') ?? 'false';
                const emptyOnReload = elem.getAttribute('data-empty-on-reload') ?? 'false';
                if (stableElement === 'false') return elem;
                if (emptyOnReload === 'true') {
                    elem.innerHTML = '';
                    elem.style.cssText = '';
                    elem.className = '';
                }
            })
                .filter(isNotUndefined)
                .forEach(element => element.parentNode?.removeChild(element));

            triggerEvent(SystemSlotEvent.SDG_SLOT_CALLING, slotEventDetails);
            triggerHTMLElementEvent(adslot.containerNode.parentNode, SlotEvent.SLOT_CALLING, slotEventDetails);
        });

        /**
         * signals slot is finished, rendered, tracked and polished. No further steps needed.
         */
        this.onState(QueueState.DONE, async () => {
            triggerEvent(SystemSlotEvent.SDG_SLOT_DONE, slotEventDetails);
            triggerHTMLElementEvent(adslot.containerNode.parentNode, SlotEvent.SLOT_DONE, slotEventDetails);
            triggerHTMLElementEvent(adslot.containerNode, SlotEvent.SLOT_DONE, slotEventDetails) //backwards compatibility for stickyfooter script, which listens for events on getContainer(), returning containerNode NOT its parentNode.
            this.resetContext();
        });
    }
}
